import { ProteinTagValues } from 'constants/segmentation';
import { CatProfile, PetProfile, isCatProfile } from 'types/pet-profile';

export type ProteinPreferenceChoice = 'dislike' | 'neutral' | 'like';
export type ProteinPreferenceChoices = Partial<Record<ProteinTagValues, ProteinPreferenceChoice>>;

export interface ProteinPreferences {
  liked: ProteinTagValues[];
  disliked: ProteinTagValues[];
}

export const CANONICAL_CAT_PROTEINS = Object.values(ProteinTagValues);

const CANONICAL_CAT_PROTEIN_SET = new Set<unknown>(CANONICAL_CAT_PROTEINS);

const normalizeProteinList = (proteins: unknown): ProteinTagValues[] => {
  if (!Array.isArray(proteins)) return [];

  return proteins.filter(
    (protein, index): protein is ProteinTagValues =>
      CANONICAL_CAT_PROTEIN_SET.has(protein) && proteins.indexOf(protein) === index
  );
};

/**
 * Normalizes the two persisted protein arrays. Existing likes win if legacy or
 * malformed state contains the same protein in both arrays.
 */
export const normalizeProteinPreferences = ({
  liked,
  disliked,
}: Partial<ProteinPreferences>): ProteinPreferences => {
  const normalizedLiked = normalizeProteinList(liked);
  const likedSet = new Set(normalizedLiked);

  return {
    liked: normalizedLiked,
    disliked: normalizeProteinList(disliked).filter((protein) => !likedSet.has(protein)),
  };
};

export const proteinPreferencesToChoices = ({
  liked,
  disliked,
}: Partial<ProteinPreferences>): ProteinPreferenceChoices => {
  const normalized = normalizeProteinPreferences({ liked, disliked });

  return {
    ...Object.fromEntries(normalized.disliked.map((protein) => [protein, 'dislike'] as const)),
    ...Object.fromEntries(normalized.liked.map((protein) => [protein, 'like'] as const)),
  };
};

export const proteinChoicesToPreferences = (
  choices: ProteinPreferenceChoices
): ProteinPreferences => ({
  liked: CANONICAL_CAT_PROTEINS.filter((protein) => choices[protein] === 'like'),
  disliked: CANONICAL_CAT_PROTEINS.filter((protein) => choices[protein] === 'dislike'),
});

export const haveProteinPreferencesChanged = (
  current: Partial<ProteinPreferences>,
  next: Partial<ProteinPreferences>
): boolean => {
  const normalizedCurrent = normalizeProteinPreferences(current);
  const normalizedNext = normalizeProteinPreferences(next);

  return (
    normalizedCurrent.liked.length !== normalizedNext.liked.length ||
    normalizedCurrent.disliked.length !== normalizedNext.disliked.length ||
    normalizedCurrent.liked.some((protein) => !normalizedNext.liked.includes(protein)) ||
    normalizedCurrent.disliked.some((protein) => !normalizedNext.disliked.includes(protein))
  );
};

/** Applies the shared normalization contract without touching allergies or other profile fields. */
export const applyProteinPreferences = (
  profile: PetProfile,
  preferences: Partial<ProteinPreferences>
): PetProfile => {
  if (!isCatProfile(profile)) return profile;

  const normalized = normalizeProteinPreferences(preferences);
  return {
    ...profile,
    selectedTastePreferences: normalized.liked,
    selectedDislikedProteins: normalized.disliked,
  } satisfies CatProfile;
};

/** Derives both persisted arrays once the sheet needs a submittable profile. */
export const applyProteinPreferenceChoices = (
  profile: PetProfile,
  choices: ProteinPreferenceChoices
): PetProfile => applyProteinPreferences(profile, proteinChoicesToPreferences(choices));

export const setProteinPreferenceChoice = ({
  choices,
  protein,
  choice,
}: {
  choices: ProteinPreferenceChoices;
  protein: ProteinTagValues;
  choice: ProteinPreferenceChoice;
}): ProteinPreferenceChoices => ({
  ...choices,
  [protein]: choice,
});

/** Supplies the backward-compatible empty exclusion list to stored cat profiles. */
export const normalizePetProfileProteinPreferences = (profile: PetProfile): PetProfile => {
  if (!isCatProfile(profile)) return profile;

  const normalized = normalizeProteinPreferences({
    liked: profile.selectedTastePreferences,
    disliked: profile.selectedDislikedProteins,
  });

  return {
    ...profile,
    selectedTastePreferences: normalized.liked,
    selectedDislikedProteins: normalized.disliked,
  } satisfies CatProfile;
};
